import React, { useContext, useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { App, Form, Button, Alert, Row, Col, Divider } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import { getCreateLocationData } from '../../../../graphql/custom-queries'
import * as logger from '../../../../services/logger'
import { statutoryPoliciesForPlace } from '../../../../util/location-facts'
import { useLeaveTypesApi } from '../../../../services/api/leave-type-templates-hook'

import { useAppSelector } from '../../../../store/hooks'
import { selectFeatureFlagsSlice } from '../../../../store/feature-flags-slice'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { useEffectivePlan } from '../../../../store/use-effective-plan'
import { selectAuthUserSlice } from '../../../../store/auth-user-slice'
import { selectLocaleSlice } from '../../../../store/locale-slice'
import { notificationStore } from '../../../../context/notificationsContext/store'
import { getTimeZoneOrDefaultToUtc } from '@vacationtracker/shared/functions/timezone'
import { NUMBER_OF_LOCATIONS_LIMIT } from '@vacationtracker/shared/data/app-parameters'

import IntlMessages from '../../../../util/IntlMessages'
import { FrontendUrls } from '../../../../types/urls'
import { PageHeader } from '../../../../design-system/PageHeader'
import CircularProgress from '../../../../components/circular-progress'
import LocationForm from '../../../../components/location-form'

import { IGetLocationById, IGetCreateLocationData, IGetLocationsIds, IGetUserListShort } from '../../../../types/locations'
import { SubscriptionPlan, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import isArray from 'lodash/isArray'
import { isWorkDayFeatureAllowed } from '@vacationtracker/shared/functions/work-week'
import { AddonTypeEnum } from '@vacationtracker/shared/types/addons'
import { clearWizardState } from '../../../../util/onboarding-wizard-storage'
import { findCountry } from '@vacationtracker/shared/data/countries'

/** The defaults a new location starts from. Nothing fetches into this: editing lives on the
 *  location page, and this route is create-only. */
const NEW_LOCATION: IGetLocationById = {
  id: '',
  name: '',
  workWeek: [1, 2, 3, 4, 5],
  timestamp: '',
  resetQuotas: 'FISCAL_YEAR',
  firstDayOfWeek: 1,
  yearStartDay: 1,
  yearStartMonth: 1,
  timezone: getTimeZoneOrDefaultToUtc(),
  isDefault: false,
  users: [],
}

const LocationFormPage = (): React.ReactElement => {
  const { message, notification } = App.useApp()
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const history = useHistory()
  // A country chosen before arriving, from "Create location in {country}" on the Locations page.
  const [newLocation] = useState<IGetLocationById>(() => ({
    ...NEW_LOCATION,
    countryCode: findCountry(new URLSearchParams(history.location.search).get('country') ?? '')?.iso,
  }))
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)

  const [isLoading, setIsLoading] = useState(true)
  const [createLoader, setCreateLoader] = useState(false)
  const [users, setUsers] = useState<IGetUserListShort[]>([])
  const [totalLocation, setTotalLocations] = useState<number>(1)
  const [existingLocations, setExistingLocations] = useState<IGetLocationsIds[]>([])
  const [allowUnlimitedLocations, setAllowUnlimitedLocations] = useState(false)
  const [leaveTypes, setLeaveTypes] = useState<IGetCreateLocationData['getLeaveTypeList']>([])

  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const effectivePlan = useEffectivePlan()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const { locale } = useAppSelector(selectLocaleSlice)
  // Same inputs and same helper the form's hint uses, so the hint and this alert can never both
  // show. tanstack dedupes the leave-laws query, so this costs no extra request.
  const watchedCountry = Form.useWatch('countryCode', form)
  const watchedSubdivision = Form.useWatch('subdivisionCode', form)
  const { data: leaveLaws } = useLeaveTypesApi()
  const willAssignLeavePolicies =
    statutoryPoliciesForPlace({ place: { countryCode: watchedCountry, subdivisionCode: watchedSubdivision ?? null }, leaveTypes, laws: leaveLaws, plan: authCompany?.plan })
      .length > 0

  const [getCreateLocationDataQuery] = useManualQuery<IGetCreateLocationData>(getCreateLocationData)

  useEffect(() => {
    fetchTotalLocationsAndUsers()
  }, [])

  useEffect(() => {
    if (authCompany?.plan === SubscriptionPlanEnum.freemium) {
      return history.push(FrontendUrls.locations)
    }
  }, [authCompany?.plan])

  const fetchTotalLocationsAndUsers = async () => {
    try {
      const response = await getCreateLocationDataQuery()
      if (!response.data || response.error) throw response.error

      setTotalLocations(response.data.getLocationList.length)
      setLeaveTypes(response.data.getLeaveTypeList ?? [])
      setExistingLocations(response.data.getLocationList)
      setUsers(response.data.getUsersFiltered.users)
      if (response.data?.getAddonsSubscription) {
        setAllowUnlimitedLocations(() => {
          return response.data?.getAddonsSubscription?.addons.find((addon) => addon.type === AddonTypeEnum.NO_LIMIT_LOCATION)?.status === 'ACTIVE'
        })
      }
      setIsLoading(false)
    } catch (err) {
      logger.error('error fetching total locations', err)
    }
  }

  const handleSubmit = async () => {
    const values = await form.validateFields()
    try {
      setCreateLoader(true)
      const woorkWeek = values.workWeek
      // TODO workday - when we add UI for custom work hours per day per location, change this:
      const workHours = isArray(values.workHours) ? values.workHours[0][1] : values.workHours
      delete values.workWeek
      delete values.workHours

      values.days = woorkWeek
      // TODO workday - when we add UI for custom work hours per day per location, change this:
      values.workHours = workHours ? woorkWeek.map((day) => [day, workHours]) : null

      const response = await Api.post('/core/event', {
        eventType: 'LOCATION_CREATED',
        eventGroup: 'LOCATION',
        ...values,
        // Let the backend seed holidays and statutory leave policies once it has accepted the
        // location, so a plan-limit rejection leaves neither behind and closing the tab cannot
        // lose them.
        importPublicHolidays: true,
        assignLeavePolicies: true,
        locale: locale.locale,
      })

      clearWizardState(authUser.id, authCompany?.id)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'location.createInProgress' }, { locationName: response.name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])

      setCreateLoader(false)
      // The holidays and leave policies above are seeded by events that land after this one, so
      // the list needs to know which card is still filling in rather than showing it as empty.
      history.push(FrontendUrls.locations, { settlingLocationId: response.locationId })
      form.resetFields()
    } catch (error) {
      logger.warning(error)
      setCreateLoader(false)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      message.error(error)
      const errorDescription = error.response?.data?.message ? error.response?.data.message : error.message ? error.message : JSON.stringify(error)
      notification.error({
        message: formatMessage({ id: 'error.generic' }),
        description: errorDescription,
        duration: 0,
      })
    }
  }

  const backToLocations = () => {
    history.push(FrontendUrls.locations)
  }

  const shouldDisableCreatingNewLocation = (companyFeatureFlags, companyPlan, locationCount, numberOfLocationsLimit, allowUnlimited) => {
    if (companyFeatureFlags.includes(FeatureFlagEnum.noLimitLocation)) {
      return false
    }
    return companyPlan === SubscriptionPlanEnum.core && locationCount >= numberOfLocationsLimit && !allowUnlimited
  }

  return (
    <div className="main-content form-background">
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <PageHeader title={<IntlMessages id="locationForm.createLocation" />} parent={[{ label: <IntlMessages id="app.locations" />, to: FrontendUrls.locations }]} />
          <div className="main-content-body">
            <div className="form-layout">
              {shouldDisableCreatingNewLocation(featureFlags, effectivePlan, totalLocation, NUMBER_OF_LOCATIONS_LIMIT, allowUnlimitedLocations) && (
                <Alert
                  style={{ marginBottom: 20 }}
                  message={
                    <IntlMessages
                      id="locationForm.locationLimitReached"
                      values={{ limitNumber: NUMBER_OF_LOCATIONS_LIMIT, billingPage: (...chunks) => <Link to="/app/settings/billing">{chunks}</Link> }}
                    />
                  }
                  type="warning"
                />
              )}
              <Form
                form={form}
                name="locationForm"
                initialValues={newLocation}
                className="location-from"
                layout="vertical"
                requiredMark="optional"
                labelWrap
                onFinish={handleSubmit}
                disabled={shouldDisableCreatingNewLocation(featureFlags, effectivePlan, totalLocation, NUMBER_OF_LOCATIONS_LIMIT, allowUnlimitedLocations)}
                scrollToFirstError
                size="large"
              >
                <LocationForm
                  location={newLocation}
                  existingLocations={existingLocations}
                  users={users}
                  form={form}
                  plan={effectivePlan as SubscriptionPlan}
                  leaveTypes={leaveTypes}
                  workdayEnabled={isWorkDayFeatureAllowed(featureFlags, authCompany)}
                  disableAll={shouldDisableCreatingNewLocation(featureFlags, effectivePlan, totalLocation, NUMBER_OF_LOCATIONS_LIMIT, allowUnlimitedLocations)}
                />
                <Divider />
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col span={24}>
                    <Form.Item style={{ textAlign: 'right' }}>
                      <Button type="default" style={{ marginRight: 10 }} onClick={backToLocations}>
                        <IntlMessages id="app.cancel" />
                      </Button>
                      <Button htmlType="submit" type="primary" loading={createLoader}>
                        <IntlMessages id="locationForm.createLocation" />
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              {!willAssignLeavePolicies && <Alert style={{ whiteSpace: 'pre-line' }} message={<IntlMessages id="locationForm.assignLeaveTypesInfo" />} type="info" />}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default LocationFormPage
